import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { getPickingLists, linkJobPicking, updateProdJobStage, uploadJobStageFile, getJobStageFile, advanceProdJobStage, getProductionJob, autoGeneratePicking } from '../../services/api';
import { STAGES, optionLabel, fmtDateTime, fmtDuration, stageDurationMs } from './stages';
import { toast } from 'sonner';
import { CheckCircle2, Circle, Clock, Upload, Eye, ArrowRight, FileCheck, Wand2 } from 'lucide-react';

const StageIcon = ({ status }) => {
  if (status === 'done') return <CheckCircle2 size={18} className="text-green-600" />;
  if (status === 'in_progress') return <Clock size={18} className="text-amber-500" />;
  return <Circle size={18} className="text-muted-foreground" />;
};

const JobDetailDialog = ({ jobId, open, onOpenChange, onChanged }) => {
  const [job, setJob] = useState(null);
  const [pickingLists, setPickingLists] = useState([]);
  const [operator, setOperator] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!jobId) return;
    try {
      const [j, pl] = await Promise.all([getProductionJob(jobId), getPickingLists()]);
      setJob(j.data);
      setPickingLists(pl.data);
    } catch (e) { toast.error('Failed to load job'); }
  }, [jobId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const refresh = (data) => { setJob(data); onChanged && onChanged(); };

  const stages = job ? job.stages : [];
  const items = job ? job.items : [];
  const current = job ? stages.find((s) => s.key === job.current_stage) : null;
  const currentDef = current ? STAGES.find((s) => s.key === current.key) : null;
  const currentOptions = currentDef && currentDef.options ? currentDef.options : [];

  // Stage timeline: duration per stage + the slowest completed stage (bottleneck)
  const doneDurations = stages.filter((s) => s.status === 'done').map((s) => stageDurationMs(s) || 0);
  const maxDuration = doneDurations.length ? Math.max(...doneDurations) : 0;
  const totalDone = doneDurations.reduce((a, b) => a + b, 0);

  const handleLinkPicking = async (plId) => {
    try { const res = await linkJobPicking(job.id, plId); refresh(res.data); toast.success('Picking list linked'); }
    catch (e) { toast.error('Failed to link picking list'); }
  };

  const handleAutoPicking = async () => {
    try {
      const res = await autoGeneratePicking(job.id);
      refresh(res.data);
      const pl = await getPickingLists();
      setPickingLists(pl.data);
      toast.success(`Created & linked ${res.data.created_picking_number} — refine materials in Warehousing`);
    } catch (e) { toast.error('Failed to auto-generate picking list'); }
  };

  const handleChoice = async (val) => {
    try { const res = await updateProdJobStage(job.id, current.key, { choice: val }); refresh(res.data); }
    catch (e) { toast.error('Failed to save selection'); }
  };

  const handleApprove = async () => {
    try { const res = await updateProdJobStage(job.id, current.key, { approved: true }); refresh(res.data); toast.success('Approved'); }
    catch (e) { toast.error('Failed to approve'); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try { const res = await uploadJobStageFile(job.id, current.key, file); refresh(res.data); toast.success('Uploaded'); }
    catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const viewStageFile = async (stageKey) => {
    try {
      const res = await getJobStageFile(job.id, stageKey);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error('Could not open the file'); }
  };

  const handleAdvance = async () => {
    setBusy(true);
    try {
      const res = await advanceProdJobStage(job.id, { operator: operator || null });
      refresh(res.data);
      setOperator('');
      toast.success(job.current_stage === 'qc' ? 'Job completed' : 'Advanced to next stage');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot advance yet');
    } finally { setBusy(false); }
  };

  const pickingReady = job && job.picking_status === 'completed';
  const openPickings = pickingLists.filter((p) => p.status === 'open');
  const allPickings = pickingLists;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="job-detail-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {job ? job.job_number : 'Job'}
            {job && <span className={`px-2 py-0.5 rounded-full text-xs ${job.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{job.status === 'completed' ? 'Completed' : 'In Progress'}</span>}
            {job && job.dispatch_day && <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 capitalize">{job.dispatch_day} · {job.target_dispatch}</span>}
          </DialogTitle>
          <DialogDescription id="job-detail-desc">{job ? `${job.customer} · ${job.units} unit(s) · ${items.length} line item(s)` : 'Loading job…'}</DialogDescription>
        </DialogHeader>
        {job && (
          <>
            {/* Items summary */}
            <div className="text-xs text-muted-foreground">
              {items.map((it, i) => (
                <span key={i} className="inline-block mr-3">{it.code ? `${it.code} ` : ''}{it.item_type} {it.size} × {it.quantity}</span>
              ))}
            </div>

            {/* Stage stepper */}
            <div className="border rounded divide-y">
              {stages.map((s) => {
                const isCurrent = job.current_stage === s.key && job.status !== 'completed';
                const dur = stageDurationMs(s);
                const isBottleneck = s.status === 'done' && maxDuration > 0 && dur === maxDuration && stages.filter((x) => x.status === 'done').length > 1;
                return (
                  <div key={s.key} className={`p-3 ${isCurrent ? 'bg-amber-50/60' : ''}`} data-testid={`stage-${s.key}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <StageIcon status={s.status} />
                        <span className="font-medium text-sm">{s.label}</span>
                        {s.choice && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{optionLabel(s.key, s.choice)}</span>}
                        {s.file && <button onClick={() => viewStageFile(s.key)} className="text-xs text-blue-600 flex items-center gap-1" data-testid={`view-stage-file-${s.key}`}><Eye size={12} />{s.file.file_name}</button>}
                        {s.approved && <span className="text-xs text-green-600 flex items-center gap-1"><FileCheck size={12} />Approved</span>}
                      </div>
                      <div className="text-xs text-muted-foreground text-right">
                        {(s.status === 'done' || (isCurrent && s.started_at)) && (
                          <div className={isBottleneck ? 'text-red-600 font-semibold' : ''} data-testid={`stage-duration-${s.key}`}>
                            {isBottleneck ? '⚠ ' : ''}{fmtDuration(dur)}{isCurrent && s.status !== 'done' ? ' so far' : ''}
                          </div>
                        )}
                        {s.operator && <div>By {s.operator}</div>}
                        {s.completed_at && <div>{fmtDateTime(s.completed_at)}</div>}
                      </div>
                    </div>

                    {/* Current stage actions */}
                    {isCurrent && (
                      <div className="mt-3 space-y-2 pl-6">
                        {currentDef?.kind === 'picking' && (
                          <div className="space-y-2">
                            <p className="text-xs text-muted-foreground">Link a picking list — it must be <b>Completed</b> to advance. {job.picking_number ? `Linked: ${job.picking_number} (${job.picking_status})` : 'None linked'}</p>
                            {!job.picking_list_id && (
                              <Button size="sm" variant="outline" onClick={handleAutoPicking} data-testid="auto-picking-btn"><Wand2 size={14} className="mr-1" />Auto-generate from job items</Button>
                            )}
                            <Select value={job.picking_list_id || ''} onValueChange={handleLinkPicking}>
                              <SelectTrigger className="h-8" data-testid="link-picking-select"><SelectValue placeholder="Select a picking list" /></SelectTrigger>
                              <SelectContent>
                                {allPickings.map((p) => <SelectItem key={p.id} value={p.id}>{p.picking_number} — {p.status}{p.reference ? ` (${p.reference})` : ''}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            {!pickingReady && job.picking_list_id && <p className="text-xs text-amber-600">Complete this picking list in Warehousing → Picking Lists, then advance.</p>}
                          </div>
                        )}

                        {currentDef?.kind === 'upload_approve' && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <label className="inline-flex items-center gap-1 text-sm border rounded px-3 py-1.5 cursor-pointer hover:bg-muted/40" data-testid={`upload-stage-${s.key}`}>
                                <Upload size={14} />{uploading ? 'Uploading…' : (s.file ? 'Replace file' : `Upload ${s.label}`)}
                                <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid={`upload-input-${s.key}`} />
                              </label>
                              {s.file && !s.approved && <Button size="sm" variant="outline" onClick={handleApprove} data-testid={`approve-stage-${s.key}`}><FileCheck size={14} className="mr-1" />Approve</Button>}
                            </div>
                          </div>
                        )}

                        {currentDef?.kind === 'choice' && (
                          <Select value={s.choice || ''} onValueChange={handleChoice}>
                            <SelectTrigger className="h-8" data-testid={`choice-${s.key}`}><SelectValue placeholder={`Select ${s.label} route`} /></SelectTrigger>
                            <SelectContent>
                              {currentOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}

                        <div className="flex items-center gap-2 pt-1">
                          <Input placeholder="Operator name" value={operator} onChange={(e) => setOperator(e.target.value)} className="h-8 w-48" data-testid="stage-operator" />
                          <Button size="sm" className="btn-accent" onClick={handleAdvance} disabled={busy} data-testid="advance-stage-btn">
                            {s.key === 'qc' ? 'Complete Job' : <>Advance <ArrowRight size={14} className="ml-1" /></>}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {totalDone > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="timeline-total">Total time in completed stages: <b>{fmtDuration(totalDone)}</b>{maxDuration > 0 && <> · slowest stage marked ⚠</>}</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JobDetailDialog;
